import TagsMenuItem from '@/components/common/tags/tags-tmpl'
import PlayerSetup from '@/components/pages/player/player-setup/player-setup-tmpl'
import schedulePlaytimeMenuItem from '@/components/common/media-menu/schedule-playtime-menu-item/schedule-playtime-menu-item-tmpl'
import CommonOptionsMixin from '@/components/mixins/common-options-mixin'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import * as JsSearch from 'js-search'
import _ from 'lodash'

const SETUP_PROPERTIES = {
  singleOutput: [
    {
      id: 'serialNumber',
      name: 'label.serialNumber',
      editable: true
    },
    {
      id: 'screen',
      name: 'label.screenOrientation',
      editable: true
    },
    {
      id: 'resolution',
      name: 'label.resolution',
      editable: true
    },
    {
      id: 'timezone',
      name: 'label.timeZone',
      editable: true
    },
    {
      id: 'audio',
      name: 'label.audio',
      editable: true
    },
    {
      id: 'led',
      name: 'LED & Video Wall',
      editable: true,
      disabled: false
    },
    {
      id: 'playerTag',
      name: 'label.tag',
      editable: true
    },
    {
      id: 'playerPlaytime',
      name: 'label.screenOn',
      editable: true
    },
    {
      id: 'status',
      name: 'label.status',
      editable: true
    },
    {
      id: 'setup',
      name: 'label.setup',
      editable: true
    }
  ],
  multiOutput: [
    {
      id: 'serialNumber',
      name: 'label.serialNumber',
      editable: true
    },
    {
      id: 'timezone',
      name: 'label.timeZone',
      editable: true
    },
    {
      id: 'audio',
      name: 'label.audio',
      editable: true
    },
    {
      id: 'led',
      name: 'LED & Video Wall',
      editable: true,
      disabled: false
    },
    {
      id: 'playerTag',
      name: 'label.tag',
      editable: true
    },
    {
      id: 'playerPlaytime',
      name: 'label.screenOn',
      editable: true
    },
    {
      id: 'status',
      name: 'label.status',
      editable: true
    },
    {
      id: 'setup',
      name: 'label.setup',
      editable: true
    }
  ]
}

const MULTI_OUTPUT_PLAYERS = ['iBX440']

export default {
  name: 'playerMenu',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError'
  ],
  components: {
    TagsMenuItem,
    schedulePlaytimeMenuItem,
    PlayerSetup
  },
  props: {
    tagsSummaryProp: {
      type: Array,
      default () { return [] }
    },
    selectedTagsFromSliderProp: {
      type: Array,
      default () { return [] }
    },
    playerSelectionProp: {
      type: Array,
      default () { return [] }
    },
    allLEDTagsProp: {
      type: Array,
      default () { return [] }
    },
    playerSetupSerial: {
      type: String,
      default () { return null }
    }
  },
  watch: {
    tagsSummaryProp: {
      handler (newVal) {
        this.handlePlayerSelectionPropData(this.playerSelectionProp)
        this.tagsSummary = newVal
      },
      deep: true
    },
    playerSelectionProp: {
      handler (newVal) {
        this.handlePlayerSelectionPropData(newVal)
      },
      deep: true
    },
    allLEDTagsProp: {
      handler (newVal) {
        this.allLEDTags = newVal
      },
      deep: true
    },
    editedProperties: {
      handler (newVal) {
        // Set Unsaved Changes
        if (newVal.length) {
          this.setUnsavedDataFlagIntoLocalstorage({ page: 'playersPage' })
        }
      },
      deep: true
    },
    playerSetupSerial: {
      immediate: true,
      handler (newVal, oldVal) {
        if (!this.serialNumber && newVal && newVal !== oldVal) {
          this.serialNumber = newVal
          this.playerSetupWizardOpen = true
        }
      }
    }
  },
  data () {
    return {
      serialNumber: '',
      enablePlayersTags: false,
      enableLEDPlayersTags: true,
      playerSetupWizardOpen: false,
      enableSetupPlayerAction: false,
      enablePlayTimes: false,
      error: '',
      setupPropertyIds: Object.freeze({
        serialNumber: 'serialNumber',
        firmwareVersion: 'firmwareVersion',
        playerTag: 'playerTag',
        playerPlaytime: 'playerPlaytime',
        status: 'status',
        setup: 'setup',
        resolution: 'resolution',
        screen: 'screen',
        led: 'led',
        audio: 'audio',
        timezone: 'timezone'
      }),
      setupProperties: SETUP_PROPERTIES.singleOutput,
      showMenuItems: false,
      tagsSummary: this.tagsSummaryProp,
      playerSelection: this.playerSelectionProp,
      playerAllTags: [],
      playerNormalTags: [],
      playerLEDTags: [],
      allLEDTags: this.allLEDTagsProp,
      showResolutionOptionProp: false,
      showScreenProp: false,
      showTimeZoneProp: false,
      hideResolutionLessThanLedDimension: false,
      resolutionOptions: {
        optionsA: [{ label: '720p', value: '1280x720', visible: true }, { label: '1080p', value: '1920x1080', visible: true }, { label: 'CUST', value: 'CUST', visible: true }],
        optionsB: [{ label: 'HD', value: '1920x1080', visible: true }, { label: '4K', value: '3840x2160', visible: true }, { label: 'CUST', value: 'CUST', visible: true }]
      },
      screenOptions: [
        { label: '', value: 'horizontal', elClass: 'horizontal' },
        { label: '', value: 'rotateLeft', elClass: 'rotateLeft' },
        { label: '', value: 'rotateRight', elClass: 'rotateRight' },
        { label: '', value: 'flipVert', elClass: 'flipVert' }
      ],
      resolutionOptionsKey: 'optionsA',
      resolutionCUST: 'CUST', // A default value when player resolution is not present from pre-defined resolutions
      selectedResolution: null,
      selectedScreen: null,
      selectedTimezone: null,
      resolutionDefaultValContent: this.$t('label.resolution'),
      showResolutionOptionContent: false,
      audioOptions: {
        optionsA: [
          { label: 'none', value: JSON.stringify({ 'enable-audio': false, 'audio-power-save': false, 'audio-output': 'hdmi' }) },
          { label: 'HDMI', value: JSON.stringify({ 'enable-audio': true, 'audio-power-save': true, 'audio-output': 'hdmi' }) },
          { label: 'line out', value: JSON.stringify({ 'enable-audio': true, 'audio-power-save': true, 'audio-output': 'jack' }) }
        ],
        optionsB: []
      },
      audioOptionsKey: 'optionsA',
      showAudioOptionProp: false,
      showAudioOptionContent: false,
      showScreenContent: false,
      showTimezoneContent: false,
      selectedAudioVal: null,
      audioDefaultValContent: this.$t('label.audio'),
      mediaPropertiesForm: {
        values: {
          players: {},
          schedules: [],
          scheduleSelection: [],
          schedulePlaytimeTarget: [],
          playtimeItemsVisible: 'all',
          scheduleNewTarget: {},
          scheduleNewTargetOptions: [],
          scheduleNewTargetDropdownVisible: false,
          scheduleNewTargetIconVisible: false
        }
      },
      showAutoLabelForPlaytime: false,
      isTimezoneSearching: false,
      timeZones: [],
      playerSetupPayload: {
        config: {}
      },
      editedProperties: [],
      userPlan: {
        premium: false,
        enterprise: false
      },
      playerReconfiguration: false
    }
  },
  mixins: [CommonOptionsMixin, UnsavedDataMixin],
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('player-menu-change', { action, payload: { data } })
      }
    },
    setDefaultData () {
      this.enablePlayersTags = this.$currentUser.hasGroupExtended()
      this.enableLEDPlayersTags = true
      this.enableSetupPlayerAction = this.$currentUser.getRights().userPlayer.setupPlayer
      this.showResolutionOptionProp = false
      this.showScreenProp = false
      this.showTimeZoneProp = false
      this.hideResolutionLessThanLedDimension = false
      this.showAudioOptionProp = false
      this.showScreenContent = false
      this.showTimezoneContent = false
      this.enablePlayTimes = this.$currentUser.hasGroupExtended()
      this.editedProperties = []
      this.playerSetupPayload = {
        config: {}
      }

      this.userPlan = {
        premium: this.$currentUser.isPremiumUser(),
        enterprise: this.$currentUser.hasGroupEnterprise()
      }
    },
    handleSubmitPlayerSetupData (params = {}) {
      const afterApiRequestAction = (responseType, serialNumber, responseData, requestData) => {
        // Update player status in the list
        const updatedPlayerSelection = this.playerSelection.map(player => {
          if (player.serialNumber === serialNumber) {
            let updatedPlayer = player

            if (responseType === 'success') {
              const updatedKeys = _.keys(requestData.config ?? {})
              if (updatedKeys.length > 0) {
                // patch current player configuration with valus from the API response
                const updatedData = _.pickBy(responseData.config, (_, key) => updatedKeys.includes(key))
                updatedPlayer.config = {
                  ...player.config,
                  ...updatedData
                }
              } else {
                updatedPlayer = responseData
              }
              updatedPlayer._edit = player._edit
            } else {
              updatedPlayer._edit.error = responseData
            }

            updatedPlayer._edit.apiRequest.status = 'done'

            return updatedPlayer
          } else {
            return player
          }
        })
        this.playerSelection = updatedPlayerSelection

        // Actions if all requests are done
        const hasPendingRequests = this.playerSelection.find((player) => player._edit.apiRequest && player._edit.apiRequest.status === 'sending')
        if (!hasPendingRequests) {
          this.removeUnsavedDataFlagInLocalStorage()
          this.editedProperties = []

          // Send data to parent component
          this.emitEvent('PLAYER_SETUP_DATA_UPDATED', { playerSelection: this.playerSelection })

          // Run callback
          if (params.callback) {
            params.callback()
          } else {
            this.$utils.mainLoader(false)
          }
        }
      }

      // Added timeout to catch all data from onBlur events before sending to backend
      this.$utils.mainLoader(true)
      window.setTimeout(() => {
        for (const player of this.playerSelection) {
          const requestData = {}
          const isConfigPropertiesChanged = _.intersection([this.setupPropertyIds.screen, this.setupPropertyIds.resolution, this.setupPropertyIds.audio, this.setupPropertyIds.timezone], this.editedProperties).length !== 0
          const isTagPropertiesChanged = _.intersection([this.setupPropertyIds.playerTag, this.setupPropertyIds.led], this.editedProperties).length !== 0
          const isPlaytimePropertiesChanged = _.intersection([this.setupPropertyIds.playerPlaytime], this.editedProperties).length !== 0

          // Player Tags
          if (isTagPropertiesChanged) {
            requestData.tags = this.playerSetupPayload.tags
          }

          // Player Playtime changed
          if (isPlaytimePropertiesChanged) {
            requestData.playTimes = this.playerSetupPayload.playTimes
          }

          // Player Config changed
          if (isConfigPropertiesChanged) {
            requestData.config = { ...this.playerSetupPayload.config }

            // Adjust resolution config if there is a LED tag
            const hasLEDTag = requestData.tags && requestData.tags.find((tag) => tag.startsWith('LED:'))

            if (hasLEDTag && (requestData.config && requestData.config['display-video-mode'])) {
              requestData.config['display-video-mode']['vertical-freq'] = 60
              requestData.config['display-video-mode']['restrict-type'] = 'hdmi'
            }
          }

          const apiRequest = () => {
            // Api request flag
            player._edit.apiRequest = {
              status: 'sending',
              responseType: null,
              responseData: null
            }

            this.$api.put(`${CONFIG.API_BASE_URL}/player/${player.serialNumber}`, requestData)
              .then(response => {
                afterApiRequestAction('success', player.serialNumber, response.data, requestData)
              })
              .catch((error) => {
                afterApiRequestAction('error', player.serialNumber, error, requestData)
                this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
              })
          }
          apiRequest()
        }
      }, 100)
    },
    handleResolutionTypeOnClick (res) {
      // Config should not be updated if the resolution value is equal to CUST
      if (res.value === this.resolutionCUST) return

      if (this.selectedResolution !== res.value) {
        this.selectedResolution = res.value
        this.filterResolutionOptions({ resolutionOptions: this.resolutionOptions, hideCUST: true })
        this.editedProperties.push(this.setupPropertyIds.resolution)

        const updatedPlayerSetupPayload = this.$utils.clone(this.playerSetupPayload)

        if (!updatedPlayerSetupPayload.config['display-video-mode']) {
          updatedPlayerSetupPayload.config['display-video-mode'] = {}
        }

        updatedPlayerSetupPayload.config['display-video-mode'].resolution = this.selectedResolution
        updatedPlayerSetupPayload.config['display-video-mode']['restrict-type'] = 'hdmi'
        this.playerSetupPayload = updatedPlayerSetupPayload
      }
    },
    updateConfigInPlayerSetupPayload (params) {
      if (params.value) {
        const updatedPlayerSetupPayload = this.$utils.clone(this.playerSetupPayload)

        if (params.screen) {
          updatedPlayerSetupPayload.config['display-orientation'] = params.value
          this.editedProperties.push(this.setupPropertyIds.screen)
        }

        if (params.timezone) {
          updatedPlayerSetupPayload.config.timezone = params.value
          this.editedProperties.push(this.setupPropertyIds.timezone)
        }

        this.playerSetupPayload = updatedPlayerSetupPayload
      }
    },
    filterResolutionOptions ({ resolutionOptions, hideCUST = false }) {
      for (const key in resolutionOptions) {
        for (const resolution of resolutionOptions[key]) {
          resolution.visible = true

          if (resolution.value === this.resolutionCUST && ((this.resolutionDefaultValContent !== this.resolutionCUST) || hideCUST)) {
            resolution.visible = false
          }
        }
      }

      return resolutionOptions
    },
    handleScreenTypeClick (screenVal) {
      this.selectedScreen = screenVal.value
      this.updateConfigInPlayerSetupPayload({ screen: true, value: this.selectedScreen })
    },
    handleDefaultSetupValueOnClick (item) {
      if (item === this.setupPropertyIds.audio) {
        if (!this.showAudioOptionContent) {
          this.showAudioOptionContent = true
        } else {
          this.showAudioOptionContent = false
        }
      }

      if (item === this.setupPropertyIds.resolution) {
        if (!this.showResolutionOptionContent) {
          this.showResolutionOptionContent = true
        } else {
          this.showResolutionOptionContent = false
        }
      }

      if (item === this.setupPropertyIds.screen) {
        if (!this.showScreenContent) {
          this.showScreenContent = true
        } else {
          this.showScreenContent = false
        }
      }

      if (item === this.setupPropertyIds.timezone) {
        if (!this.showTimezoneContent) {
          this.showTimezoneContent = true
        } else {
          this.showTimezoneContent = false
        }
      }
    },
    handleAudioOnChange (audioVal, elType) {
      let audio = audioVal

      if (elType === 'dropDown') {
        audio = {
          value: {
            'audio-output': audioVal,
            'audio-power-save': true,
            'enable-audio': !!audioVal
          }
        }

        audio.value = JSON.stringify(audio.value)
      }

      if (this.selectedAudioVal !== audio.value) {
        this.selectedAudioVal = audio.value

        if (elType === 'dropDown') {
          this.selectedAudioVal = audioVal
        }

        this.editedProperties.push(this.setupPropertyIds.audio)

        const updatedPlayerSetupPayload = this.$utils.clone(this.playerSetupPayload)
        updatedPlayerSetupPayload.config = { ...updatedPlayerSetupPayload.config, ...JSON.parse(audio.value) }
        this.playerSetupPayload = updatedPlayerSetupPayload
      }
    },
    handleTimezoneOnChange ($event) {
      this.updateConfigInPlayerSetupPayload({ timezone: true, value: this.selectedTimezone })
    },
    handleTimezoneSearch (timeZone) {
      if (timeZone !== '') {
        this.isTimezoneSearching = true

        const timezones = this.$utils.clone(this.customTimeZone())
        const search = new JsSearch.Search('value')
        search.addIndex('label')
        search.addDocuments(timezones)

        this.timeZones = search.search(timeZone)
        this.isTimezoneSearching = false
      }
    },
    extractPlayerTags () {
      const playerAllTags = this.$utils.clone(this.playerAllTags)
      this.playerNormalTags = playerAllTags.filter((tag) => { return !tag.includes('LED:') })
      this.playerLEDTags = playerAllTags.filter((tag) => { return tag.includes('LED:') })
    },
    getPlayersSerialNumber (players) {
      return players.map(p => p.serialNumber)
    },
    handlePlayerSelectionPropData (newSelection) {
      let isSelectionChanged = !_.isEqual(this.getPlayersSerialNumber(this.playerSelection), this.getPlayersSerialNumber(newSelection))

      /**
       * Check if player selection not change but player tags changed.
       * Commonly happens when updating player tags at the top filter of players page.
       */
      if (!isSelectionChanged && _.isEqual(this.playerSelection.length > 0, newSelection.length > 0)) {
        const prevTags = this.playerSelection.map(p => p.tags.map(t => t))
        const newTags = newSelection.map(p => p.tags.map(t => t))
        if (!_.isEqual(prevTags, newTags)) {
          isSelectionChanged = true
        }
      }

      if (!isSelectionChanged) return

      if (isSelectionChanged) {
        this.setDefaultData()
      }

      this.showMenuItems = false
      this.playerSelection = newSelection

      const singlePlayerSelected = this.playerSelection.length === 1
      const multiplePlayerSelected = this.playerSelection.length > 1
      const showSetupSettings = (singlePlayerSelected && this.playerSelection[0].status === 'unconfigured')

      // SET PLAYER TAGS
      let tagSummary = []

      if (singlePlayerSelected) {
        tagSummary = this.playerSelection[0].tags ? this.playerSelection[0].tags : []
        this.selectedScreen = (this.playerSelection[0].config && this.playerSelection[0].config['display-orientation']) ? this.playerSelection[0].config['display-orientation'] : 'horizontal'
        this.selectedTimezone = (this.playerSelection[0].config && this.playerSelection[0].config.timezone) ? this.playerSelection[0].config.timezone : ''
        this.showScreenProp = true
        this.showTimeZoneProp = true
        this.enableSetupPlayerAction = false

        if (showSetupSettings) {
          this.enableSetupPlayerAction = true
          this.enablePlayersTags = false
          this.enablePlayTimes = false
          this.enableLEDPlayersTags = false
          this.showResolutionOptionProp = false
          this.showAudioOptionProp = false
          this.showScreenProp = false
          this.showTimeZoneProp = false
        }
      }

      if (multiplePlayerSelected) {
        const clonePlayerSelection = this.$utils.clone(this.playerSelection)
        const tagsCompSrc = clonePlayerSelection[0].tags ? clonePlayerSelection[0].tags.sort().join(',') : []

        const allTagsTheSame = clonePlayerSelection.every((player) => {
          return (player.tags ? player.tags.sort() : []).join(',') === tagsCompSrc
        })

        if (allTagsTheSame) {
          tagSummary = clonePlayerSelection[0].tags
        }
      }

      // No player selected
      if (!singlePlayerSelected && !multiplePlayerSelected) {
        this.enableSetupPlayerAction = true
      }

      this.playerAllTags = tagSummary
      this.extractPlayerTags()

      // SET PLAYERS GROUP FOR CONFIG (resolution and audio)
      const groupAPlayerType = ['DiVA', 'HMP300', 'HMP350']
      const groupBPlayerType = ['DSOS', 'HMP400', 'HMP400W', 'iBX440', 'iBX410', 'iBX410W']

      // Set resolution options
      const hasOptionsA = this.playerSelection.find((player) => { return groupAPlayerType.indexOf(player.deviceType) >= 0 })
      const hasOptionsB = this.playerSelection.find((player) => { return groupBPlayerType.indexOf(player.deviceType) >= 0 })
      let showResolutionOptionPropVal = false
      let resolutionOptionsKeyVal = null

      if (hasOptionsA && !hasOptionsB) {
        showResolutionOptionPropVal = true
        resolutionOptionsKeyVal = 'optionsA'
      } else if (!hasOptionsA && hasOptionsB) {
        showResolutionOptionPropVal = true
        resolutionOptionsKeyVal = 'optionsB'
      }

      this.showResolutionOptionProp = showResolutionOptionPropVal && !showSetupSettings
      this.resolutionOptionsKey = resolutionOptionsKeyVal

      // Set resolution default value
      if (this.showResolutionOptionProp) {
        const resolutionSummary = []
        this.playerSelection.forEach((player) => {
          if (player.config && player.config['display-video-mode']) {
            const displayVideoMode = player.config['display-video-mode']
            if (displayVideoMode.resolution) {
              resolutionSummary.push(displayVideoMode.resolution)
            } else if (Array.isArray(displayVideoMode) && displayVideoMode.length) {
              if (displayVideoMode[0] && displayVideoMode[0]?.resolution) {
                resolutionSummary.push(displayVideoMode[0].resolution)
              } else {
                // If it's an array with initial value null, get resolution from second element
                if (displayVideoMode[1] && displayVideoMode[1]?.resolution) {
                  resolutionSummary.push(displayVideoMode[1].resolution)
                }
              }
            }
          }
        })

        if (resolutionSummary.length === 0) {
          this.showResolutionOptionProp = false
        }

        this.selectedResolution = resolutionSummary[0]
      }

      // Set Audio Default value and Options for group A
      this.audioOptionsKey = 'optionsA'

      const audioSummary = []
      this.playerSelection.forEach((player) => {
        const audioSettings = {}
        audioSettings['enable-audio'] = (player.config && player.config['enable-audio']) ? player.config && player.config['enable-audio'] : false
        audioSettings['audio-power-save'] = (player.config && player.config['audio-power-save']) ? player.config && player.config['audio-power-save'] : false
        audioSettings['audio-output'] = (player.config && player.config['audio-output']) ? player.config && player.config['audio-output'] : 'hdmi'

        audioSummary.push(JSON.stringify(audioSettings))
      })

      this.showAudioOptionProp = false
      this.selectedAudioVal = null
      if (audioSummary.length === 1) {
        this.showAudioOptionProp = !showSetupSettings
        this.selectedAudioVal = audioSummary[0]
      } else if (audioSummary.length > 1) {
        const theSameAudioData = audioSummary.every((conf) => { return conf === audioSummary[0] })

        if (theSameAudioData) {
          this.showAudioOptionProp = !showSetupSettings
          this.selectedAudioVal = audioSummary[0]
        }
      }

      // Set Audio Default value and Options for group B
      const allGroupBPlayers = this.playerSelection.filter((player) => { return groupBPlayerType.indexOf(player.deviceType) >= 0 })
      if (allGroupBPlayers.length && (allGroupBPlayers.length === this.playerSelection.length)) {
        const audioValIdsSummary = []
        allGroupBPlayers.forEach((player) => {
          if (player.audioInfo) {
            player.audioInfo.sort()
            let audioId = ''
            player.audioInfo.forEach((audio, indx) => { audioId += `${audio.id}, ` })
            audioValIdsSummary.push(audioId)
          } else {
            audioValIdsSummary.push(null)
          }
        })

        const allOptionsAreTheSame = audioValIdsSummary.every((audioId) => { return audioId === audioValIdsSummary[0] })
        const allAudioValueTheSame = allGroupBPlayers.every((player) => { return (player.config && allGroupBPlayers[0].config) && (player.config['audio-output'] === allGroupBPlayers[0].config['audio-output']) })

        if (allOptionsAreTheSame && allAudioValueTheSame) {
          const audioOptions = this.$utils.clone(this.audioOptions)
          audioOptions.optionsB = (allGroupBPlayers[0] && allGroupBPlayers[0].audioInfo) ? allGroupBPlayers[0].audioInfo.filter((audio) => { return audio.name }) : []

          // The default value of audio-output is false and display as NONE in the ui.
          let newSelectedAudioVal = false
          if (allGroupBPlayers[0].config && (allGroupBPlayers[0].config['audio-output'] && allGroupBPlayers[0].config['enable-audio'])) {
            newSelectedAudioVal = allGroupBPlayers[0].config['audio-output']
          }

          // Add None Option
          if (audioOptions.optionsB.length || newSelectedAudioVal) {
            const noneOption = {
              available: '',
              desc: '',
              id: false,
              name: 'none'
            }
            audioOptions.optionsB.push(noneOption)
          }

          this.audioOptions = audioOptions
          this.showAudioOptionProp = !showSetupSettings
          this.audioOptionsKey = 'optionsB'
          this.selectedAudioVal = newSelectedAudioVal
        }
      }

      // Set Default value content
      this.showAudioOptionContent = false
      this.showResolutionOptionContent = false
      this.audioTitle = this.$t('label.audio')
      this.resolutionDefaultValContent = 'none'
      this.audioDefaultValContent = 'none'

      // Resolution config value
      if (this.showResolutionOptionProp) {
        const currentResOptions = this.resolutionOptions[this.resolutionOptionsKey]
        const getResolution = this.selectedResolution

        if (getResolution) {
          const findResTitle = currentResOptions.find((option) => { return option.value === getResolution })

          /**
           * If resolution is present from predefined resolutions (720p|1080p| or HD|4K) show the resolution label, else show the CUST.
           * If the label is CUST, CUST option will be visible in resolution options handled in filterResolutionOptions()
           */
          if (findResTitle && findResTitle.label) {
            this.resolutionDefaultValContent = findResTitle.label
          } else {
            this.resolutionDefaultValContent = this.resolutionCUST
          }
        }

        // Set default
        if (!getResolution) {
          if (this.resolutionOptionsKey === 'optionsA') {
            const findResTitle = currentResOptions.find((option) => { return option.value === '1920x1080' })
            this.resolutionDefaultValContent = findResTitle.label
          }

          if (this.resolutionOptionsKey === 'optionsB') {
            const findResTitle = currentResOptions.find((option) => { return option.value === '3840x2160' })
            this.resolutionDefaultValContent = findResTitle.label
          }
        }

        this.resolutionOptions = this.filterResolutionOptions({ resolutionOptions: this.resolutionOptions })
      }

      // Audio config value
      if (this.showAudioOptionProp) {
        let getAudio = this.selectedAudioVal

        if (getAudio) {
          const findResTitle = this.audioOptions[this.audioOptionsKey].find((option) => { return option.value === getAudio || option.id === getAudio })
          let audioDefaultValContent = 'none'

          if (findResTitle && (findResTitle.label || findResTitle.name)) {
            audioDefaultValContent = findResTitle.label || findResTitle.name
            if (audioDefaultValContent === 'line-out') getAudio = getAudio.replace('-', ' ')
          }

          this.audioDefaultValContent = audioDefaultValContent
        }

        // Set default
        if (!getAudio) {
          this.audioDefaultValContent = 'none'
        }
      }

      this.compareResolutionFromLedDimension({ tags: this.playerNormalTags.concat(this.playerLEDTags) })

      // SET PLAYER PLAYTIMES
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const playerSchedules = []
      let hasObjectPlaytime = false

      if (this.playerSelection.length) {
        this.playerSelection.forEach((player, indx) => {
          const isPlaytimeAnArray = Array.isArray(player.playTimes)
          const isPlaytimeAnObject = !isPlaytimeAnArray && (typeof player.playTimes === 'object')

          if (isPlaytimeAnArray) {
            playerSchedules.push({
              custom: player.playTimes ? player.playTimes : [],
              type: 'custom',
              order: indx,
              target: player.serialNumber
            })
          } else if (isPlaytimeAnObject) {
            hasObjectPlaytime = true
          }
        })

        // SET PLAYER PLAYTIMES AS AUTO
        this.showAutoLabelForPlaytime = false
        if (hasObjectPlaytime) {
          this.showAutoLabelForPlaytime = true
        }
      }

      mediaPropertiesForm.values.scheduleSelection = playerSchedules
      this.mediaPropertiesForm = mediaPropertiesForm

      this.$nextTick(() => {
        this.showMenuItems = this.playerSelection.length > 0

        if (this.showMenuItems && MULTI_OUTPUT_PLAYERS.includes(this.playerSelection[0].deviceType)) {
          this.setupProperties = SETUP_PROPERTIES.multiOutput
        } else {
          this.setupProperties = SETUP_PROPERTIES.singleOutput
        }
      })
    },
    handleNewMenuItemClick () {
      this.playerSetupWizardOpen = true
    },
    handleSetupMenuItemClick () {
      this.serialNumber = this.playerSelection[0].serialNumber
      this.playerSetupWizardOpen = true
      this.playerReconfiguration = true
    },
    handleTagsMenuItemChange ($event) {
      if ($event.action === 'TAGS_UPDATED' || $event.action === 'TAG_REMOVED') {
        if ($event.payload.accessorComponent === 'playerNormalTagsMenuItem') {
          this.playerNormalTags = $event.payload.data.tags
          this.editedProperties.push(this.setupPropertyIds.playerTag)
        } else if ($event.payload.accessorComponent === 'playerLEDTagsMenuItem') {
          this.playerLEDTags = $event.payload.data.tags
          this.editedProperties.push(this.setupPropertyIds.led)
        }

        // Update player tags to add/remove LED tag and make sure no empty tags
        let newPlayerAllTags = this.$utils.clone(this.playerNormalTags).concat(this.$utils.clone(this.playerLEDTags))
        newPlayerAllTags = newPlayerAllTags.filter((tag) => { return tag.trim().length })
        this.playerAllTags = newPlayerAllTags

        const updatedPlayerSetupPayload = this.$utils.clone(this.playerSetupPayload)
        updatedPlayerSetupPayload.tags = newPlayerAllTags
        this.playerSetupPayload = updatedPlayerSetupPayload
      }
    },
    compareResolutionFromLedDimension (params) {
      const currentResolution = this.selectedResolution
      const res = {
        smallerThanLedDimension: false,
        resolutionValue: this.selectedResolution
      }
      this.hideResolutionLessThanLedDimension = false

      /*
        Hide the RESOLUTION entry
        If the resolution was set to a size smaller then the LED size, update it.
        - Resolution was set to 720, and the LED:widthxheight width is bigger than 1280 or the height is bigger then 720, then automatically switch to 1080
        - Resolution was set to HD, and the LED:widthxheight width is bigger than 1920 or the height is bigger then 1080, then automatically switch to 4K
          Update the Resolution tag to use “720p” and “1080p” as label otherwise.
      */
      const getLEDTAG = params.tags.find((tag) => { return tag.includes('LED:') })
      if (getLEDTAG) {
        const ledWidth = getLEDTAG.substr(4).split('x')[0]
        const ledHeight = getLEDTAG.substr(4).split('x')[1]
        let currentResolutionWidth = 0
        this.hideResolutionLessThanLedDimension = true

        if (currentResolution) {
          currentResolutionWidth = currentResolution.split('x')[0]

          if (parseInt(ledWidth) > parseInt(currentResolutionWidth) || parseInt(ledHeight) > parseInt(currentResolutionWidth)) {
            res.smallerThanLedDimension = true
            this.hideResolutionLessThanLedDimension = true
          }
        }
      } else {
        this.hideResolutionLessThanLedDimension = false
      }

      return res
    },
    handlePlaytimeChange ($event) {
      const acceptedEventsToSavePlaytime = ['PLAYTIMES_UPDATED', 'PLAYTIME_FORM_CLOSED'].indexOf($event.action) >= 0
      const acceptedDOMEventsToSavePlaytime = ['addNewPlaytimeButtonClick', 'removePlaytimeIconClick', 'playTimeFormClosed'].indexOf($event.payload.data.domEvent) >= 0

      if (acceptedEventsToSavePlaytime && acceptedDOMEventsToSavePlaytime) {
        const payloadData = this.$utils.clone($event.payload.data)
        const playTimes = payloadData.playtimes

        // Cleanup playtimes
        for (const indx in playTimes) {
          if (playTimes[indx]._edit) {
            delete playTimes[indx]._edit
          }
        }

        // Keep mediaPropertiesForm data updated
        const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
        mediaPropertiesForm.values.scheduleSelection = []

        this.playerSelection.forEach((player, indx) => {
          if (player.playTimes) {
            mediaPropertiesForm.values.scheduleSelection.push({
              custom: player.playTimes,
              type: 'custom',
              order: indx,
              target: player.serialNumber
            })
          }
        })
        this.mediaPropertiesForm = mediaPropertiesForm

        this.editedProperties.push(this.setupPropertyIds.playerPlaytime)

        const updatedPlayerSetupPayload = this.$utils.clone(this.playerSetupPayload)
        updatedPlayerSetupPayload.playTimes = playTimes
        this.playerSetupPayload = updatedPlayerSetupPayload
      }
    },
    handleDownloadSetupConfig () {
      this.$utils.downloadDocument({
        url: '/static/docs/ARYA Setup config for HMP3xx.7z',
        dataType: 'application/xml',
        label: 'ARYA Setup config for HMP3xx.7z'
      })
    },
    handleCloseSetupPlayerDialog () {
      this.serialNumber = ''
      this.playerSetupWizardOpen = false
      this.playerReconfiguration = false
      this.$emit('PlayerAdded')
    }
  },
  mounted () {
    this.setDefaultData()
  }
}
